import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import clawEaseMark from "../../assets/flow/clawease-mark.png";
import { usePageActivity } from "../../hooks/usePageActivity";
import emailChannelLogo from "../../assets/flow/channel-email.png";
import phoneChannelLogo from "../../assets/flow/channel-phone.png";
import webChatChannelLogo from "../../assets/flow/channel-webchat.png";
import whatsAppChannelLogo from "../../assets/flow/channel-whatsapp-official.svg";
import aditLogo from "../../assets/flow/pms-adit.png";
import careStackLogo from "../../assets/flow/pms-carestack.png";
import cloud9Logo from "../../assets/flow/pms-cloud9.png";
import curveDentalLogo from "../../assets/flow/pms-curve-dental.png";
import dentaltapLogo from "../../assets/flow/pms-dentaltap.png";
import denticonLogo from "../../assets/flow/pms-denticon.png";
import dentimaxLogo from "../../assets/flow/pms-dentimax.png";
import dentosLogo from "../../assets/flow/pms-dentos.png";
import dentrixAscendLogo from "../../assets/flow/pms-dentrix-ascend.png";
import eaglesoftLogo from "../../assets/flow/pms-eaglesoft.png";
import openDentalLogo from "../../assets/flow/pms-open-dental.png";
import oryxLogo from "../../assets/flow/pms-oryx.png";
import senseiCloudLogo from "../../assets/flow/pms-sensei-cloud.png";
import siftDentalLogo from "../../assets/flow/pms-siftdental.png";
import tab32Logo from "../../assets/flow/pms-tab32.png";

const STAGE_WIDTH = 720;
const STAGE_HEIGHT = 430;

type Point = { x: number; y: number };
type AvatarName = "maya" | "leo" | "nina" | "iris";

type RouteEdge = {
  id: string;
  kind: "channel" | "orbit" | "pms";
  points: Point[];
};

type MeasuredRouteEdge = RouteEdge & { length: number };

type RouteLocation = {
  edgeId: string;
  edgeOffset: number;
  offRouteDistance: number;
  pathDistance: number;
  pathId: string;
  strength: number;
};

type RouteDot = {
  edgeId: string;
  edgeOffset: number;
  kind: RouteEdge["kind"];
  point: Point;
};

type RoutePath = {
  edgeIds: string[];
  edgeStarts: Map<string, number>;
  id: string;
};

type PausableTimelineState = {
  elapsedSeconds: number;
  running: boolean;
};

type AvatarPalette = {
  background: string;
  hair: string;
  shirt: string;
  skin: string;
};

type PmsLogo = {
  height: number;
  label: string;
  logo: string;
  opacity: number;
  width: number;
  x: number;
  y: number;
  zIndex: number;
};

const avatarPalettes: Record<AvatarName, AvatarPalette> = {
  maya: { background: "#f1e6ff", hair: "#5b3677", shirt: "#8b5be5", skin: "#eab995" },
  leo: { background: "#e5f2ff", hair: "#26384b", shirt: "#428fde", skin: "#9a674c" },
  nina: { background: "#e4f8ef", hair: "#2d262c", shirt: "#2db27a", skin: "#d99b70" },
  iris: { background: "#e8f8fa", hair: "#7c503b", shirt: "#32a8b9", skin: "#f0c5a0" },
};

const orbitCenter: Point = { x: 398, y: 235 };
const INNER_ORBIT_RADIUS = 78;
const INNER_ORBIT_STEPS = 24;
const channelOrbitContact: Point = { x: orbitCenter.x - INNER_ORBIT_RADIUS, y: orbitCenter.y };
const PMS_DESTINATIONS = [
  {
    absorptionLeadDistance: 33,
    control1: { x: 506, y: 235 },
    control2: { x: 555, y: 241 },
    label: "Dentaltap",
    routeAnchor: { x: 590, y: 241 },
  },
  {
    absorptionLeadDistance: 45.6,
    control1: { x: 476, y: 211 },
    control2: { x: 515, y: 168 },
    label: "DentOS",
    routeAnchor: { x: 548, y: 162 },
  },
  {
    absorptionLeadDistance: 46.5,
    control1: { x: 476, y: 269 },
    control2: { x: 503, y: 339 },
    label: "CareStack",
    routeAnchor: { x: 535, y: 353 },
  },
] as const satisfies ReadonlyArray<{
  absorptionLeadDistance: number;
  control1: Point;
  control2: Point;
  label: string;
  routeAnchor: Point;
}>;
type PmsDestinationLabel = (typeof PMS_DESTINATIONS)[number]["label"];
const DEFAULT_PMS_DESTINATION: PmsDestinationLabel = PMS_DESTINATIONS[0].label;
const MESSAGE_INTERVAL = 0.8;

const channels = [
  {
    avatar: "maya" as const,
    color: "#8151ea",
    delay: MESSAGE_INTERVAL,
    label: "Email",
    logo: emailChannelLogo,
    message: "Make appointment tomorrow at 9:00",
    orbitSide: "lower" as const,
    y: 46,
  },
  {
    avatar: "leo" as const,
    color: "#438fe4",
    delay: 0,
    label: "Phone",
    logo: phoneChannelLogo,
    message: "I have severe tooth pain today",
    orbitSide: "lower" as const,
    y: 152,
  },
  {
    avatar: "nina" as const,
    color: "#28b879",
    delay: MESSAGE_INTERVAL * 2,
    label: "WhatsApp",
    logo: whatsAppChannelLogo,
    message: "Can I move my cleaning to Friday?",
    orbitSide: "upper" as const,
    y: 258,
  },
  {
    avatar: "iris" as const,
    color: "#2ea4b7",
    delay: MESSAGE_INTERVAL * 3,
    label: "Web Chat",
    logo: webChatChannelLogo,
    message: "Do you accept my dental insurance?",
    orbitSide: "upper" as const,
    y: 364,
  },
];

const CHANNEL_CYCLE_DURATION = MESSAGE_INTERVAL * channels.length;
const PMS_SEQUENCE_DURATION = CHANNEL_CYCLE_DURATION * PMS_DESTINATIONS.length;
const AVATAR_COPIES_PER_CHANNEL = PMS_DESTINATIONS.length * 2;
const FLOW_DURATION = CHANNEL_CYCLE_DURATION * AVATAR_COPIES_PER_CHANNEL;
// Keep roughly the same spatial gap between avatars after tightening the
// message cadence from 1.2s to 0.8s.
const TRAJECTORY_SPEED = 82.5;
const ROUTE_DOT_SPACING = 9;
const ROUTE_GLOW_CORE_RADIUS = 9;
const ROUTE_GLOW_RADIUS = 48;
const ROUTE_GLOW_MAX_OPACITY = 0.9;
const ROUTE_AVATAR_OPACITY_THRESHOLD = 0.08;
const ROUTE_DOT_COLOR = "#b8c0cc";
// Finish the destination cross-fade before the first avatar commits to the
// outgoing PMS branch, so the eye reads "target selected" before movement.
const PMS_ACTIVATION_LEAD_SECONDS = 0.6;
const PMS_LOGO_PULSE_LEAD_SECONDS = 0.16;
// Fade while still moving, ending outside the logo at the moment its pulse peaks.
const AVATAR_ABSORPTION_FADE_DISTANCE =
  TRAJECTORY_SPEED * PMS_LOGO_PULSE_LEAD_SECONDS;
// The route highlight follows the 60fps avatar transform. The expensive DOM
// geometry reads are gone, so matching the original visual cadence no longer
// reintroduces the old layout cost.
const ROUTE_LIGHT_FRAME_INTERVAL_MS = 1000 / 60;
const AVATAR_MOUNT_LEAD_SECONDS = 0.18;
const AVATAR_MOUNT_TAIL_SECONDS = 0.08;
const AVATAR_FADE_SECONDS = 0.16;
const AVATAR_RELEASE_START_SECONDS = CHANNEL_CYCLE_DURATION * 1.05;
const AVATAR_RELEASE_END_SECONDS = CHANNEL_CYCLE_DURATION * 1.08;
const MAX_CHANNEL_INGRESS_DISTANCE = Math.max(
  ...channels.map((channel) =>
    Math.hypot(channelOrbitContact.x - 252, channelOrbitContact.y - (channel.y + 30)),
  ),
);
const SYNCHRONIZED_ORBIT_ENTRY_SECONDS =
  AVATAR_RELEASE_END_SECONDS + MAX_CHANNEL_INGRESS_DISTANCE / TRAJECTORY_SPEED;
const MESSAGE_REACHES_TRANSFORMER = 0.54;
// Hold the opening frame just before the first phone message reaches its
// transformer. This keeps the hero populated without overlapping the card.
const INITIAL_TIMELINE_OFFSET_SECONDS =
  CHANNEL_CYCLE_DURATION * MESSAGE_REACHES_TRANSFORMER - 0.08;
const MESSAGE_CONSUMED_AT = 0.96;
const TRANSFORMER_GLOW_START = 0.42;
const TRANSFORMER_GLOW_PEAK = 0.69;
const TRANSFORMER_GLOW_END = 0.87;
const LOGO_PULSE_START = 0.42;
const LOGO_PULSE_PEAK = 0.66;
const LOGO_PULSE_END = 0.9;
const WAVE_RANDOM_STEPS = 6;
const WAVE_BAR_HEIGHTS = [7, 15, 10, 19, 12, 17, 8, 14, 20, 11, 16];

const pmsLogos: PmsLogo[] = [
  {
    height: 54,
    label: DEFAULT_PMS_DESTINATION,
    logo: dentaltapLogo,
    opacity: 0.54,
    width: 145,
    x: 575,
    y: 214,
    zIndex: 32,
  },
  {
    height: 22,
    label: "tab32",
    logo: tab32Logo,
    opacity: 0.3,
    width: 58,
    x: 616,
    y: 64,
    zIndex: 20,
  },
  {
    height: 30,
    label: "DentOS",
    logo: dentosLogo,
    opacity: 0.55,
    width: 92,
    x: 536,
    y: 147,
    zIndex: 26,
  },
  {
    height: 28,
    label: "Dentrix Ascend",
    logo: dentrixAscendLogo,
    opacity: 0.58,
    width: 94,
    x: 706,
    y: 160,
    zIndex: 27,
  },
  {
    height: 24,
    label: "SiftDental",
    logo: siftDentalLogo,
    opacity: 0.42,
    width: 94,
    x: 659,
    y: 105,
    zIndex: 24,
  },
  {
    height: 28,
    label: "Open Dental",
    logo: openDentalLogo,
    opacity: 0.7,
    width: 88,
    x: 736,
    y: 243,
    zIndex: 29,
  },
  {
    height: 42,
    label: "Curve Dental",
    logo: curveDentalLogo,
    opacity: 0.54,
    width: 58,
    x: 673,
    y: 324,
    zIndex: 25,
  },
  {
    height: 28,
    label: "CareStack",
    logo: careStackLogo,
    opacity: 0.5,
    width: 120,
    x: 511,
    y: 339,
    zIndex: 24,
  },
  {
    height: 20,
    label: "Denticon",
    logo: denticonLogo,
    opacity: 0.34,
    width: 82,
    x: 511,
    y: 430,
    zIndex: 21,
  },
  {
    height: 23,
    label: "Eaglesoft",
    logo: eaglesoftLogo,
    opacity: 0.3,
    width: 74,
    x: 644,
    y: 447,
    zIndex: 20,
  },
  {
    height: 24,
    label: "DentiMax",
    logo: dentimaxLogo,
    opacity: 0.2,
    width: 80,
    x: 518,
    y: 18,
    zIndex: 18,
  },
  {
    height: 25,
    label: "Sensei Cloud",
    logo: senseiCloudLogo,
    opacity: 0.22,
    width: 96,
    x: 688,
    y: 22,
    zIndex: 19,
  },
  {
    height: 30,
    label: "Adit",
    logo: aditLogo,
    opacity: 0.4,
    width: 62,
    x: 521,
    y: 101,
    zIndex: 23,
  },
  {
    height: 28,
    label: "Oryx Dental",
    logo: oryxLogo,
    opacity: 0.43,
    width: 54,
    x: 739,
    y: 379,
    zIndex: 22,
  },
  {
    height: 18,
    label: "Cloud 9",
    logo: cloud9Logo,
    opacity: 0.23,
    width: 78,
    x: 560,
    y: 510,
    zIndex: 17,
  },
];

function leftPercent(value: number) {
  return `${(value / STAGE_WIDTH) * 100}%`;
}

function topPercent(value: number) {
  return `${(value / STAGE_HEIGHT) * 100}%`;
}

function rectStyle(x: number, y: number, width: number, height: number): CSSProperties {
  return {
    height: topPercent(height),
    left: leftPercent(x),
    top: topPercent(y),
    width: leftPercent(width),
  };
}

function pointStyle(point: Point): CSSProperties {
  return { left: leftPercent(point.x), top: topPercent(point.y) };
}

function pointOnOrbit(degrees: number): Point {
  const radians = (degrees * Math.PI) / 180;

  return {
    x: orbitCenter.x + Math.cos(radians) * INNER_ORBIT_RADIUS,
    y: orbitCenter.y + Math.sin(radians) * INNER_ORBIT_RADIUS,
  };
}

function getPmsDestination(label: PmsDestinationLabel) {
  return PMS_DESTINATIONS.find((destination) => destination.label === label) ?? PMS_DESTINATIONS[0];
}

function buildPmsRoutePoints(label: PmsDestinationLabel, steps = 12) {
  const start = pointOnOrbit(0);
  const { control1, control2, routeAnchor } = getPmsDestination(label);

  return Array.from({ length: steps + 1 }, (_, index) => {
    const progress = index / steps;
    const inverse = 1 - progress;

    return {
      x:
        inverse ** 3 * start.x +
        3 * inverse ** 2 * progress * control1.x +
        3 * inverse * progress ** 2 * control2.x +
        progress ** 3 * routeAnchor.x,
      y:
        inverse ** 3 * start.y +
        3 * inverse ** 2 * progress * control1.y +
        3 * inverse * progress ** 2 * control2.y +
        progress ** 3 * routeAnchor.y,
    };
  });
}

function buildFlowRoute(
  rowCenter: number,
  orbitSide: "lower" | "upper",
  destinationLabel: PmsDestinationLabel,
) {
  const outlet: Point = { x: 252, y: rowCenter };
  const innerOrbit = buildOrbitPoints(orbitSide);
  const pmsRoute = buildPmsRoutePoints(destinationLabel);
  const route: Point[] = [
    outlet,
    ...innerOrbit,
    ...pmsRoute.slice(1),
  ];
  const ingressTravelSeconds =
    Math.hypot(channelOrbitContact.x - outlet.x, channelOrbitContact.y - outlet.y) / TRAJECTORY_SPEED;
  const orbitEntryDelaySeconds = Math.max(
    0,
    SYNCHRONIZED_ORBIT_ENTRY_SECONDS - AVATAR_RELEASE_END_SECONDS - ingressTravelSeconds,
  );

  return { orbitEntryDelaySeconds, outlet, route };
}

function buildOrbitPoints(orbitSide: "lower" | "upper") {
  return Array.from({ length: INNER_ORBIT_STEPS + 1 }, (_, index) => {
    const orbitProgress = (180 * index) / INNER_ORBIT_STEPS;
    return pointOnOrbit(orbitSide === "lower" ? 180 - orbitProgress : 180 + orbitProgress);
  });
}

function samplePolyline(points: Point[], spacing: number) {
  const segments: Array<{ from: Point; length: number; start: number; to: Point }> = [];
  let totalLength = 0;

  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const length = Math.hypot(to.x - from.x, to.y - from.y);

    if (length < 0.001) continue;

    segments.push({ from, length, start: totalLength, to });
    totalLength += length;
  }

  const samples: Array<{ distance: number; point: Point }> = [];
  let segmentIndex = 0;

  for (let distance = spacing; distance < totalLength; distance += spacing) {
    while (
      segmentIndex < segments.length - 1 &&
      distance > segments[segmentIndex].start + segments[segmentIndex].length
    ) {
      segmentIndex += 1;
    }

    const segment = segments[segmentIndex];
    const ratio = (distance - segment.start) / segment.length;
    samples.push({
      distance,
      point: {
        x: segment.from.x + (segment.to.x - segment.from.x) * ratio,
        y: segment.from.y + (segment.to.y - segment.from.y) * ratio,
      },
    });
  }

  return { samples, totalLength };
}

function positiveModulo(value: number, period: number) {
  return ((value % period) + period) % period;
}

function resumedAnimationDelay(
  baseDelay: number,
  duration: number,
  elapsedSeconds: number,
) {
  if (elapsedSeconds < baseDelay) return baseDelay - elapsedSeconds;

  return -positiveModulo(elapsedSeconds - baseDelay, duration);
}

function usePausableTimeline(
  shouldRun: boolean,
  initialElapsedSeconds = 0,
): PausableTimelineState {
  const accumulatedSecondsRef = useRef(initialElapsedSeconds);
  const runningSinceRef = useRef<number | null>(null);
  const wasRunningRef = useRef(shouldRun);
  const [timeline, setTimeline] = useState<PausableTimelineState>({
    elapsedSeconds: initialElapsedSeconds,
    running: shouldRun,
  });

  useEffect(() => {
    const nowSeconds = performance.now() / 1000;

    if (shouldRun) {
      runningSinceRef.current = nowSeconds;

      if (!wasRunningRef.current) {
        setTimeline({
          elapsedSeconds: accumulatedSecondsRef.current,
          running: true,
        });
      }
    } else {
      if (wasRunningRef.current && runningSinceRef.current !== null) {
        accumulatedSecondsRef.current += nowSeconds - runningSinceRef.current;
      }

      runningSinceRef.current = null;

      if (wasRunningRef.current) {
        setTimeline({
          elapsedSeconds: accumulatedSecondsRef.current,
          running: false,
        });
      }
    }

    wasRunningRef.current = shouldRun;
  }, [shouldRun]);

  return timeline;
}

function routeSlug(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function channelEdgeId(label: string) {
  return `channel-${routeSlug(label)}`;
}

function pmsEdgeId(label: string) {
  return `pms-${routeSlug(label)}`;
}

function flowRoutePathId(channelLabel: string, pmsLabel: string) {
  return `${channelEdgeId(channelLabel)}-to-${pmsEdgeId(pmsLabel)}`;
}

function polylineLength(points: Point[]) {
  let length = 0;

  for (let index = 1; index < points.length; index += 1) {
    length += Math.hypot(
      points[index].x - points[index - 1].x,
      points[index].y - points[index - 1].y,
    );
  }

  return length;
}

const routeGraphEdges: RouteEdge[] = [
  ...channels.map((channel) => ({
    id: channelEdgeId(channel.label),
    kind: "channel" as const,
    points: [
      { x: 252, y: channel.y + 30 },
      channelOrbitContact,
    ],
  })),
  {
    id: "orbit-lower",
    kind: "orbit" as const,
    points: buildOrbitPoints("lower"),
  },
  {
    id: "orbit-upper",
    kind: "orbit" as const,
    points: buildOrbitPoints("upper"),
  },
  ...PMS_DESTINATIONS.map((destination) => ({
    id: pmsEdgeId(destination.label),
    kind: "pms" as const,
    points: buildPmsRoutePoints(destination.label),
  })),
];

const measuredRouteEdges: MeasuredRouteEdge[] = routeGraphEdges.map((edge) => ({
  ...edge,
  length: polylineLength(edge.points),
}));

const routeEdgeById = new Map(measuredRouteEdges.map((edge) => [edge.id, edge]));

const routePaths: RoutePath[] = channels.flatMap((channel) =>
  PMS_DESTINATIONS.map((destination) => {
    const edgeIds = [
      channelEdgeId(channel.label),
      `orbit-${channel.orbitSide}`,
      pmsEdgeId(destination.label),
    ];
    const edgeStarts = new Map<string, number>();
    let pathDistance = 0;

    edgeIds.forEach((edgeId) => {
      const edge = routeEdgeById.get(edgeId);

      edgeStarts.set(edgeId, pathDistance);
      pathDistance += edge?.length ?? 0;
    });

    return {
      edgeIds,
      edgeStarts,
      id: flowRoutePathId(channel.label, destination.label),
    };
  }),
);

const routePathById = new Map(routePaths.map((path) => [path.id, path]));

function projectPointToPolyline(point: Point, points: Point[]) {
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestOffset = 0;
  let cumulativeDistance = 0;

  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const deltaX = to.x - from.x;
    const deltaY = to.y - from.y;
    const segmentLengthSquared = deltaX * deltaX + deltaY * deltaY;
    const segmentLength = Math.sqrt(segmentLengthSquared);

    if (segmentLength < 0.001) continue;

    const progress = Math.max(
      0,
      Math.min(
        1,
        ((point.x - from.x) * deltaX + (point.y - from.y) * deltaY) /
          segmentLengthSquared,
      ),
    );
    const projectedPoint = {
      x: from.x + deltaX * progress,
      y: from.y + deltaY * progress,
    };
    const distance = Math.hypot(point.x - projectedPoint.x, point.y - projectedPoint.y);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestOffset = cumulativeDistance + segmentLength * progress;
    }

    cumulativeDistance += segmentLength;
  }

  return { distance: bestDistance, edgeOffset: bestOffset };
}

function projectPointToRoutePath(point: Point, pathId: string): RouteLocation | null {
  const path = routePathById.get(pathId);

  if (!path) return null;

  let bestProjection: RouteLocation | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  path.edgeIds.forEach((edgeId) => {
    const edge = routeEdgeById.get(edgeId);

    if (!edge) return;

    const projection = projectPointToPolyline(point, edge.points);

    if (projection.distance < bestDistance) {
      bestDistance = projection.distance;
      bestProjection = {
        edgeId: edge.id,
        edgeOffset: projection.edgeOffset,
        offRouteDistance: projection.distance,
        pathDistance: (path.edgeStarts.get(edge.id) ?? 0) + projection.edgeOffset,
        pathId,
        strength: 1,
      };
    }
  });

  return bestProjection;
}

const routeGraphDots: RouteDot[] = measuredRouteEdges.flatMap((edge) =>
  samplePolyline(edge.points, ROUTE_DOT_SPACING).samples.map((sample) => ({
    edgeId: edge.id,
    edgeOffset: sample.distance,
    kind: edge.kind,
    point: sample.point,
  })),
);

function routeGlowStrength(distance: number) {
  if (distance >= ROUTE_GLOW_RADIUS) return 0;
  if (distance <= ROUTE_GLOW_CORE_RADIUS) return ROUTE_GLOW_MAX_OPACITY;

  const progress =
    (ROUTE_GLOW_RADIUS - distance) /
    (ROUTE_GLOW_RADIUS - ROUTE_GLOW_CORE_RADIUS);
  const smoothProgress = progress * progress * (3 - 2 * progress);
  return ROUTE_GLOW_MAX_OPACITY * smoothProgress;
}

function routeDotOpacity(dot: RouteDot, sources: RouteLocation[]) {
  let opacity = 0;

  sources.forEach((source) => {
    const path = routePathById.get(source.pathId);
    const dotEdgeStart = path?.edgeStarts.get(dot.edgeId);

    if (dotEdgeStart === undefined) return;

    const distance =
      Math.abs(source.pathDistance - (dotEdgeStart + dot.edgeOffset)) +
      source.offRouteDistance;

    opacity = Math.max(
      opacity,
      routeGlowStrength(distance) * source.strength,
    );
  });

  return opacity;
}

function interpolateNumberKeyframe(values: number[], times: number[], progress: number) {
  if (progress <= times[0]) return values[0];

  for (let index = 1; index < times.length; index += 1) {
    if (progress <= times[index]) {
      const segmentDuration = times[index] - times[index - 1];
      const segmentProgress = segmentDuration > 0
        ? (progress - times[index - 1]) / segmentDuration
        : 1;

      return values[index - 1] + (values[index] - values[index - 1]) * segmentProgress;
    }
  }

  return values[values.length - 1];
}

function interpolatePointKeyframe(points: Point[], times: number[], progress: number) {
  return {
    x: interpolateNumberKeyframe(points.map((point) => point.x), times, progress),
    y: interpolateNumberKeyframe(points.map((point) => point.y), times, progress),
  };
}

function deterministicWaveValue(seed: number) {
  let value = seed | 0;
  value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
  value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
  value ^= value >>> 16;
  return 0.38 + ((value >>> 0) / 4294967295) * 0.62;
}

function buildWavePath(scales: number[]) {
  return WAVE_BAR_HEIGHTS.map((baseHeight, index) => {
    const height = Math.max(2, baseHeight * (scales[index] ?? 0.45));
    const x = 1 + index * 4;

    return `M ${x} ${12 - height / 2} V ${12 + height / 2}`;
  }).join(" ");
}

const channelWaveFrames = new Map(
  channels.map((channel) => {
    const channelSeed = Array.from(channel.label).reduce(
      (seed, character) => seed + character.charCodeAt(0),
      0,
    );
    const idleScales = WAVE_BAR_HEIGHTS.map(() => 0.45);
    const randomScaleFrames = Array.from(
      { length: WAVE_RANDOM_STEPS },
      (_, step) => WAVE_BAR_HEIGHTS.map((_, index) =>
        deterministicWaveValue(channelSeed * 101 + index * 17 + step * 37),
      ),
    );
    const randomOpacities = randomScaleFrames.map((scales) => {
      const average = scales.reduce((sum, scale) => sum + scale, 0) / scales.length;
      return 0.35 + average * 0.65;
    });
    const idlePath = buildWavePath(idleScales);

    return [
      channel.label,
      {
        opacities: [0.28, 0.28, ...randomOpacities, 0.28, 0.28],
        paths: [
          idlePath,
          idlePath,
          ...randomScaleFrames.map(buildWavePath),
          idlePath,
          idlePath,
        ],
      },
    ] as const;
  }),
);

function PatientAvatar({ name, size = 32 }: { name: AvatarName; size?: number }) {
  const palette = avatarPalettes[name];

  return (
    <span
      aria-hidden="true"
      className="relative block shrink-0 overflow-hidden rounded-full"
      style={{ backgroundColor: palette.background, height: size, width: size }}
    >
      <span
        className="absolute left-1/2 top-[18%] h-[42%] w-[42%] -translate-x-1/2 rounded-full"
        style={{ backgroundColor: palette.skin }}
      />
      <span
        className="absolute left-1/2 top-[12%] h-[25%] w-[47%] -translate-x-1/2 rounded-t-full rounded-br-[55%]"
        style={{ backgroundColor: palette.hair }}
      />
      <span
        className="absolute -bottom-[18%] left-1/2 h-[52%] w-[78%] -translate-x-1/2 rounded-t-full"
        style={{ backgroundColor: palette.shirt }}
      />
      <span className="absolute left-[39%] top-[36%] h-[3%] w-[3%] rounded-full bg-[#403235]" />
      <span className="absolute right-[39%] top-[36%] h-[3%] w-[3%] rounded-full bg-[#403235]" />
    </span>
  );
}

function AvatarToken({
  avatar,
  color,
  size = 28,
}: {
  avatar: AvatarName;
  color: string;
  size?: number;
}) {
  return (
    <span
      aria-hidden="true"
      className="flex items-center justify-center rounded-full border-2 bg-white shadow-[0_7px_18px_rgba(71,52,113,.18)]"
      style={{ borderColor: color, height: size, width: size }}
    >
      <PatientAvatar name={avatar} size={size - 6} />
    </span>
  );
}

function buildAvatarTimeline(
  rowCenter: number,
  orbitSide: "lower" | "upper",
  destinationLabel: PmsDestinationLabel,
) {
  const { orbitEntryDelaySeconds, outlet, route } = buildFlowRoute(
    rowCenter,
    orbitSide,
    destinationLabel,
  );
  const avatarReleaseStart = (AVATAR_RELEASE_START_SECONDS + orbitEntryDelaySeconds) / FLOW_DURATION;
  const avatarReleaseEnd = (AVATAR_RELEASE_END_SECONDS + orbitEntryDelaySeconds) / FLOW_DURATION;
  const arrivalTimes = [avatarReleaseEnd];
  let elapsedSeconds = AVATAR_RELEASE_END_SECONDS + orbitEntryDelaySeconds;
  let destinationDepartureSeconds = elapsedSeconds;

  for (let index = 1; index < route.length; index += 1) {
    const previous = route[index - 1];
    const current = route[index];
    elapsedSeconds += Math.hypot(current.x - previous.x, current.y - previous.y) / TRAJECTORY_SPEED;
    arrivalTimes.push(elapsedSeconds / FLOW_DURATION);

    if (index === INNER_ORBIT_STEPS + 1) {
      destinationDepartureSeconds = elapsedSeconds;
    }
  }

  const destination = route[route.length - 1];
  const { absorptionLeadDistance } = getPmsDestination(destinationLabel);
  const routeVisibility = arrivalTimes.map((arrivalTime) => {
    const remainingDistance = Math.max(
      0,
      (elapsedSeconds - arrivalTime * FLOW_DURATION) * TRAJECTORY_SPEED,
    );

    return Math.max(
      0,
      Math.min(
        1,
        (remainingDistance - absorptionLeadDistance) /
          AVATAR_ABSORPTION_FADE_DISTANCE,
      ),
    );
  });
  // The dotted route can still meet the PMS, while the patient token is
  // absorbed at a safe boundary before it can overlap the visible logo.
  const destinationArrivalSeconds =
    elapsedSeconds - absorptionLeadDistance / TRAJECTORY_SPEED;
  const absorptionHoldEnd = (elapsedSeconds + 0.07) / FLOW_DURATION;
  const fadeEnd = (elapsedSeconds + AVATAR_FADE_SECONDS) / FLOW_DURATION;
  const waypoints: Point[] = [outlet, outlet, ...route, destination, destination, destination];
  const times = [0, avatarReleaseStart, ...arrivalTimes, absorptionHoldEnd, fadeEnd, 1];
  const opacity = [0, 0, ...routeVisibility, 0, 0, 0];
  const scale = [
    0.55,
    0.7,
    ...routeVisibility.map((visibility) => 0.66 + visibility * 0.34),
    0.66,
    0.18,
    0.18,
  ];

  return {
    destinationArrivalSeconds,
    destinationDepartureSeconds,
    opacity,
    scale,
    times,
    visibleEndSeconds: destinationArrivalSeconds,
    visibleStartSeconds: AVATAR_RELEASE_START_SECONDS + orbitEntryDelaySeconds,
    waypoints,
  };
}

const pmsDestinationArrivals = PMS_DESTINATIONS.map((destination, groupIndex) => {
  const channelTimings = channels.map((channel) => {
    const timeline = buildAvatarTimeline(
      channel.y + 30,
      channel.orbitSide,
      destination.label,
    );

    return {
      arrivalSeconds: channel.delay + timeline.destinationArrivalSeconds,
      departureSeconds: channel.delay + timeline.destinationDepartureSeconds,
    };
  });

  return {
    firstActivationSeconds:
      groupIndex * CHANNEL_CYCLE_DURATION +
      Math.min(...channelTimings.map((timing) => timing.departureSeconds)) -
      PMS_ACTIVATION_LEAD_SECONDS,
    firstArrivalSeconds:
      groupIndex * CHANNEL_CYCLE_DURATION +
      Math.min(...channelTimings.map((timing) => timing.arrivalSeconds)),
    label: destination.label,
  };
});

const pmsLogoPulseEvents = PMS_DESTINATIONS.flatMap((destination, groupIndex) =>
  channels.map((channel) => ({
    firstPulseSeconds:
      groupIndex * CHANNEL_CYCLE_DURATION +
      channel.delay +
      buildAvatarTimeline(
        channel.y + 30,
        channel.orbitSide,
        destination.label,
      ).destinationArrivalSeconds -
      PMS_LOGO_PULSE_LEAD_SECONDS,
    label: destination.label,
  })),
);

type AvatarFlowDescriptor = {
  avatar: AvatarName;
  color: string;
  delay: number;
  destinationLabel: PmsDestinationLabel;
  id: string;
  orbitSide: "lower" | "upper";
  routePathId: string;
  rowCenter: number;
  timeline: ReturnType<typeof buildAvatarTimeline>;
};

type MountedAvatarEntry = {
  animationElapsedSeconds: number;
  id: string;
};

const avatarFlowDescriptors: AvatarFlowDescriptor[] = channels.flatMap((channel) =>
  Array.from({ length: AVATAR_COPIES_PER_CHANNEL }, (_, copyIndex) => {
    const destination = PMS_DESTINATIONS[copyIndex % PMS_DESTINATIONS.length];
    const rowCenter = channel.y + 30;

    return {
      avatar: channel.avatar,
      color: channel.color,
      delay: channel.delay + copyIndex * CHANNEL_CYCLE_DURATION,
      destinationLabel: destination.label,
      id: `${channel.label}-${destination.label}-flow-token-${copyIndex}`,
      orbitSide: channel.orbitSide,
      routePathId: flowRoutePathId(channel.label, destination.label),
      rowCenter,
      timeline: buildAvatarTimeline(
        rowCenter,
        channel.orbitSide,
        destination.label,
      ),
    };
  }),
);

const avatarFlowDescriptorById = new Map(
  avatarFlowDescriptors.map((descriptor) => [descriptor.id, descriptor]),
);

function avatarProgressAtElapsed(
  descriptor: AvatarFlowDescriptor,
  elapsedSeconds: number,
) {
  if (elapsedSeconds < descriptor.delay) return 0;

  return positiveModulo(
    elapsedSeconds - descriptor.delay,
    FLOW_DURATION,
  ) / FLOW_DURATION;
}

function avatarStateAtElapsed(
  descriptor: AvatarFlowDescriptor,
  elapsedSeconds: number,
) {
  const progress = avatarProgressAtElapsed(descriptor, elapsedSeconds);
  const { opacity, scale, times, waypoints } = descriptor.timeline;

  return {
    opacity: interpolateNumberKeyframe(opacity, times, progress),
    point: interpolatePointKeyframe(waypoints, times, progress),
    scale: interpolateNumberKeyframe(scale, times, progress),
  };
}

function avatarShouldBeMounted(
  descriptor: AvatarFlowDescriptor,
  elapsedSeconds: number,
) {
  const mountStart = Math.max(
    0,
    descriptor.timeline.visibleStartSeconds - AVATAR_MOUNT_LEAD_SECONDS,
  );
  const mountEnd = Math.min(
    FLOW_DURATION,
    descriptor.timeline.visibleEndSeconds + AVATAR_MOUNT_TAIL_SECONDS,
  );

  if (elapsedSeconds < descriptor.delay + mountStart) return false;

  const phase = positiveModulo(
    elapsedSeconds - descriptor.delay,
    FLOW_DURATION,
  );

  return phase >= mountStart && phase <= mountEnd;
}

function mountedAvatarIdsAtElapsed(elapsedSeconds: number) {
  return avatarFlowDescriptors
    .filter((descriptor) => avatarShouldBeMounted(descriptor, elapsedSeconds))
    .sort((left, right) => {
      const leftOpacity = avatarStateAtElapsed(left, elapsedSeconds).opacity;
      const rightOpacity = avatarStateAtElapsed(right, elapsedSeconds).opacity;

      return rightOpacity - leftOpacity;
    })
    .map((descriptor) => descriptor.id)
    .sort();
}

function nextAvatarMountBoundary(elapsedSeconds: number) {
  return Math.min(
    ...avatarFlowDescriptors.map((descriptor) => {
      const mountStart = Math.max(
        0,
        descriptor.timeline.visibleStartSeconds - AVATAR_MOUNT_LEAD_SECONDS,
      );
      const mountEnd = Math.min(
        FLOW_DURATION,
        descriptor.timeline.visibleEndSeconds + AVATAR_MOUNT_TAIL_SECONDS,
      );
      const firstMount = descriptor.delay + mountStart;

      if (elapsedSeconds < firstMount) return firstMount;

      const localElapsed = elapsedSeconds - descriptor.delay;
      const cycleIndex = Math.floor(localElapsed / FLOW_DURATION);
      const phase = positiveModulo(localElapsed, FLOW_DURATION);

      if (phase < mountStart) {
        return descriptor.delay + cycleIndex * FLOW_DURATION + mountStart;
      }

      if (phase < mountEnd) {
        return descriptor.delay + cycleIndex * FLOW_DURATION + mountEnd;
      }

      return descriptor.delay + (cycleIndex + 1) * FLOW_DURATION + mountStart;
    }),
  );
}

function useMountedAvatarDescriptors(
  running: boolean,
  elapsedSeconds: number,
) {
  const [mountedEntries, setMountedEntries] = useState<MountedAvatarEntry[]>(() =>
    mountedAvatarIdsAtElapsed(elapsedSeconds).map((id) => ({
      animationElapsedSeconds: elapsedSeconds,
      id,
    })),
  );

  useEffect(() => {
    const resumedAtSeconds = performance.now() / 1000;
    let timeoutId = 0;

    const synchronizeMountedAvatars = (realignExisting = false) => {
      const currentElapsed = elapsedSeconds + (
        running ? performance.now() / 1000 - resumedAtSeconds : 0
      );
      const nextIds = mountedAvatarIdsAtElapsed(currentElapsed);

      setMountedEntries((currentEntries) => {
        const currentById = new Map(
          currentEntries.map((entry) => [entry.id, entry]),
        );
        const nextEntries = nextIds.map((id) => {
          const currentEntry = currentById.get(id);

          return currentEntry && !realignExisting
            ? currentEntry
            : { animationElapsedSeconds: currentElapsed, id };
        });

        if (
          currentEntries.length === nextEntries.length &&
          currentEntries.every((entry, index) =>
            entry.id === nextEntries[index].id &&
            entry.animationElapsedSeconds === nextEntries[index].animationElapsedSeconds,
          )
        ) {
          return currentEntries;
        }

        return nextEntries;
      });

      if (!running) return;

      const nextBoundary = nextAvatarMountBoundary(currentElapsed);
      timeoutId = window.setTimeout(
        () => synchronizeMountedAvatars(false),
        Math.max(20, (nextBoundary - currentElapsed) * 1000 + 12),
      );
    };

    // A pause/resume changes the parent animation clock. Re-align existing
    // avatars once here, while normal mount boundaries preserve their clocks.
    synchronizeMountedAvatars(true);

    return () => window.clearTimeout(timeoutId);
  }, [elapsedSeconds, running]);

  return mountedEntries.flatMap((entry) => {
    const descriptor = avatarFlowDescriptorById.get(entry.id);

    return descriptor
      ? [{
          animationElapsedSeconds: entry.animationElapsedSeconds,
          descriptor,
        }]
      : [];
  });
}

type PmsDestinationPhase = "activation" | "arrival";

function pmsDestinationAtElapsed(
  elapsedSeconds: number,
  phase: PmsDestinationPhase,
): PmsDestinationLabel {
  let latestChange = Number.NEGATIVE_INFINITY;
  let destination = DEFAULT_PMS_DESTINATION;

  pmsDestinationArrivals.forEach((timing) => {
    const firstChangeSeconds = phase === "activation"
      ? timing.firstActivationSeconds
      : timing.firstArrivalSeconds;

    if (elapsedSeconds < firstChangeSeconds) return;

    const occurrence =
      firstChangeSeconds +
      Math.floor(
        (elapsedSeconds - firstChangeSeconds) / PMS_SEQUENCE_DURATION,
      ) *
        PMS_SEQUENCE_DURATION;

    if (occurrence > latestChange) {
      latestChange = occurrence;
      destination = timing.label;
    }
  });

  return destination;
}

function nextPmsDestinationChange(
  elapsedSeconds: number,
  phase: PmsDestinationPhase,
) {
  return Math.min(
    ...pmsDestinationArrivals.map((timing) => {
      const firstChangeSeconds = phase === "activation"
        ? timing.firstActivationSeconds
        : timing.firstArrivalSeconds;

      if (elapsedSeconds < firstChangeSeconds) {
        return firstChangeSeconds;
      }

      return (
        firstChangeSeconds +
        (Math.floor(
          (elapsedSeconds - firstChangeSeconds) / PMS_SEQUENCE_DURATION,
        ) +
          1) *
          PMS_SEQUENCE_DURATION
      );
    }),
  );
}

function usePmsDestination(
  running: boolean,
  elapsedSeconds: number,
  reducedMotion: boolean,
  phase: PmsDestinationPhase,
) {
  const [destination, setDestination] = useState<PmsDestinationLabel>(
    DEFAULT_PMS_DESTINATION,
  );

  useEffect(() => {
    if (reducedMotion) {
      setDestination(DEFAULT_PMS_DESTINATION);
      return undefined;
    }

    const resumedAtSeconds = performance.now() / 1000;
    let timeoutId = 0;

    const synchronizeDestination = () => {
      const currentElapsed =
        elapsedSeconds +
        (running ? performance.now() / 1000 - resumedAtSeconds : 0);

      setDestination(pmsDestinationAtElapsed(currentElapsed, phase));

      if (!running) return;

      const nextChange = nextPmsDestinationChange(currentElapsed, phase);
      timeoutId = window.setTimeout(
        synchronizeDestination,
        Math.max(20, (nextChange - currentElapsed) * 1000 + 12),
      );
    };

    synchronizeDestination();

    return () => window.clearTimeout(timeoutId);
  }, [elapsedSeconds, phase, reducedMotion, running]);

  return destination;
}

type PmsLogoPulse = {
  label: PmsDestinationLabel | null;
  serial: number;
};

function nextPmsLogoPulseEvent(elapsedSeconds: number) {
  return pmsLogoPulseEvents.reduce<{
    atSeconds: number;
    label: PmsDestinationLabel;
  }>((nextEvent, event) => {
    const atSeconds = elapsedSeconds < event.firstPulseSeconds
      ? event.firstPulseSeconds
      : event.firstPulseSeconds +
        (Math.floor(
          (elapsedSeconds - event.firstPulseSeconds) / PMS_SEQUENCE_DURATION,
        ) +
          1) *
          PMS_SEQUENCE_DURATION;

    return atSeconds < nextEvent.atSeconds
      ? { atSeconds, label: event.label }
      : nextEvent;
  }, {
    atSeconds: Number.POSITIVE_INFINITY,
    label: DEFAULT_PMS_DESTINATION,
  });
}

function usePmsLogoPulse(
  running: boolean,
  elapsedSeconds: number,
  reducedMotion: boolean,
) {
  const [pulse, setPulse] = useState<PmsLogoPulse>({
    label: null,
    serial: 0,
  });

  useEffect(() => {
    if (!running || reducedMotion) {
      setPulse((currentPulse) => currentPulse.label === null
        ? currentPulse
        : { ...currentPulse, label: null });
      return undefined;
    }

    const resumedAtSeconds = performance.now() / 1000;
    let timeoutId = 0;

    const scheduleNextPulse = () => {
      const currentElapsed =
        elapsedSeconds + performance.now() / 1000 - resumedAtSeconds;
      const nextEvent = nextPmsLogoPulseEvent(currentElapsed);

      timeoutId = window.setTimeout(() => {
        setPulse((currentPulse) => ({
          label: nextEvent.label,
          serial: currentPulse.serial + 1,
        }));
        scheduleNextPulse();
      }, Math.max(20, (nextEvent.atSeconds - currentElapsed) * 1000 + 12));
    };

    scheduleNextPulse();

    return () => window.clearTimeout(timeoutId);
  }, [elapsedSeconds, reducedMotion, running]);

  return pulse;
}

function PmsConstellationLogo({
  brand,
  flowActive,
  isActive,
  isPrelit,
  pulseSerial,
  reducedMotion,
}: {
  brand: PmsLogo;
  flowActive: boolean;
  isActive: boolean;
  isPrelit: boolean;
  pulseSerial: number;
  reducedMotion: boolean;
}) {
  const destinationArrival = pmsDestinationArrivals.find(
    (arrival) => arrival.label === brand.label,
  );
  return (
    <motion.div
      data-pms-logo={brand.label}
      data-pms-active={isActive ? "true" : "false"}
      data-pms-prelit={isPrelit && !isActive ? "true" : "false"}
      data-pms-first-activation={destinationArrival?.firstActivationSeconds.toFixed(3)}
      data-pms-first-arrival={destinationArrival?.firstArrivalSeconds.toFixed(3)}
      className="pointer-events-none absolute isolate flex items-center justify-center"
      style={{
        ...rectStyle(brand.x, brand.y, brand.width, brand.height),
        transformOrigin: "center center",
        zIndex: isActive ? 34 : isPrelit ? 33 : brand.zIndex,
      }}
      initial={false}
      animate={{
        opacity: isActive ? 1 : isPrelit ? 0.94 : brand.opacity,
        scale: isActive ? 1.02 : isPrelit ? 1.01 : 1,
      }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { duration: 0.42, ease: "easeOut" }
      }
    >
      <img
        src={brand.logo}
        alt=""
        aria-hidden="true"
        className="relative z-10 h-full w-full object-contain"
        draggable={false}
        style={{
          filter: "grayscale(1) saturate(0) contrast(.8)",
        }}
      />
      {destinationArrival ? (
        <motion.img
          src={brand.logo}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 z-20 h-full w-full object-contain"
          draggable={false}
          initial={false}
          animate={{ opacity: isActive ? 1 : isPrelit ? 0.88 : 0 }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : { duration: 0.42, ease: "easeOut" }
          }
        />
      ) : null}
      {destinationArrival && pulseSerial > 0 && flowActive ? (
        <motion.img
          data-pms-arrival-pulse={brand.label}
          key={`${brand.label}-arrival-pulse-${pulseSerial}`}
          src={brand.logo}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 z-30 h-full w-full object-contain"
          draggable={false}
          style={{
            filter: "brightness(1.22) saturate(1.08)",
          }}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{
            opacity: [0, 0.72, 0.28, 0],
            scale: [0.98, 1.09, 1.04, 1.02],
          }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : {
                  duration: 0.46,
                  ease: "easeOut",
                  times: [0, 0.35, 0.7, 1],
                }
          }
        />
      ) : null}
    </motion.div>
  );
}

function avatarRouteSourcesAtElapsed(elapsedSeconds: number) {
  return avatarFlowDescriptors.flatMap((descriptor) => {
    const state = avatarStateAtElapsed(descriptor, elapsedSeconds);

    if (state.opacity <= ROUTE_AVATAR_OPACITY_THRESHOLD) return [];

    const projection = projectPointToRoutePath(
      state.point,
      descriptor.routePathId,
    );

    return projection ? [{ ...projection, strength: state.opacity }] : [];
  });
}

const initialRouteSources = avatarRouteSourcesAtElapsed(
  INITIAL_TIMELINE_OFFSET_SECONDS,
);
const initialRouteDotOpacities = routeGraphDots.map((dot) =>
  routeDotOpacity(dot, initialRouteSources),
);

function FlowingAvatar({
  active,
  currentElapsedSeconds,
  descriptor,
  elapsedSeconds,
}: {
  active: boolean;
  currentElapsedSeconds: number;
  descriptor: AvatarFlowDescriptor;
  elapsedSeconds: number;
}) {
  const { opacity, scale, times, waypoints } = descriptor.timeline;
  const currentState = avatarStateAtElapsed(
    descriptor,
    active ? elapsedSeconds : currentElapsedSeconds,
  );
  const motionPoint = (point: Point) => ({
    x: `${(point.x / STAGE_WIDTH) * 100}cqw`,
    y: `${(point.y / STAGE_HEIGHT) * 100}cqh`,
  });
  const initialState = {
    ...motionPoint(currentState.point),
    opacity: currentState.opacity,
    scale: currentState.scale,
  };
  const animationDelay = resumedAnimationDelay(
    descriptor.delay,
    FLOW_DURATION,
    elapsedSeconds,
  );

  return (
    <motion.div
      aria-hidden="true"
      data-flow-avatar={descriptor.avatar}
      data-flow-base-delay={descriptor.delay.toFixed(3)}
      data-pms-destination={descriptor.destinationLabel}
      data-orbit-side={descriptor.orbitSide}
      data-route-path={descriptor.routePathId}
      className="pointer-events-none absolute left-0 top-0 z-30 h-6 w-6"
      style={{ marginLeft: -12, marginTop: -12, transformOrigin: "center" }}
      initial={initialState}
      animate={
        active
          ? {
              x: waypoints.map((point) => motionPoint(point).x),
              y: waypoints.map((point) => motionPoint(point).y),
              opacity,
              scale,
            }
          : initialState
      }
      transition={
        active
          ? {
              delay: animationDelay,
              duration: FLOW_DURATION,
              ease: "linear",
              repeat: Infinity,
              times,
            }
          : { duration: 0 }
      }
    >
      <AvatarToken
        avatar={descriptor.avatar}
        color={descriptor.color}
        size={24}
      />
    </motion.div>
  );
}

function RouteLightFieldDots() {
  return (
    <div
      aria-hidden="true"
      data-route-light-field
      className="pointer-events-none absolute inset-0 z-[15]"
    >
      {routeGraphDots.map((dot, index) => (
        <span
          key={`${dot.edgeId}-${dot.edgeOffset.toFixed(2)}`}
          data-route-light-dot={index}
          data-route-edge={dot.edgeId}
          data-route-kind={dot.kind}
          data-route-offset={dot.edgeOffset.toFixed(2)}
          data-route-x={dot.point.x.toFixed(3)}
          data-route-y={dot.point.y.toFixed(3)}
          className="absolute h-[3px] w-[3px] rounded-full"
          style={{
            ...pointStyle(dot.point),
            backgroundColor: ROUTE_DOT_COLOR,
            marginLeft: -1.5,
            marginTop: -1.5,
            opacity: initialRouteDotOpacities[index],
          }}
        />
      ))}
    </div>
  );
}

function useRouteLightField(
  stageRef: { current: HTMLDivElement | null },
  active: boolean,
  elapsedSeconds: number,
) {
  useEffect(() => {
    const stage = stageRef.current;

    if (!stage) return undefined;

    const dots = Array.from(
      stage.querySelectorAll<HTMLElement>("[data-route-light-dot]"),
    );
    let previousOpacities = [...initialRouteDotOpacities];

    const applyOpacities = (opacities: number[], force = false) => {
      dots.forEach((dot, index) => {
        const nextOpacity = opacities[index] ?? 0;

        if (
          force ||
          Math.abs(nextOpacity - (previousOpacities[index] ?? 0)) >= 0.01
        ) {
          dot.style.opacity = nextOpacity.toFixed(3);
          previousOpacities[index] = nextOpacity;
        }
      });
    };

    const updateAtElapsed = (currentElapsed: number, force = false) => {
      const sources = avatarRouteSourcesAtElapsed(currentElapsed);
      const opacities = routeGraphDots.map((dot) =>
        routeDotOpacity(dot, sources),
      );

      applyOpacities(opacities, force);
    };

    if (!active) {
      updateAtElapsed(elapsedSeconds, true);
      return undefined;
    }

    const resumedAtSeconds = performance.now() / 1000;
    let animationFrameId = 0;
    let lastFrameTime = Number.NEGATIVE_INFINITY;

    const updateLightField = (frameTime: number) => {
      if (frameTime - lastFrameTime >= ROUTE_LIGHT_FRAME_INTERVAL_MS) {
        updateAtElapsed(
          elapsedSeconds + performance.now() / 1000 - resumedAtSeconds,
        );
        lastFrameTime = frameTime;
      }

      animationFrameId = requestAnimationFrame(updateLightField);
    };

    updateAtElapsed(elapsedSeconds, true);
    animationFrameId = requestAnimationFrame(updateLightField);

    return () => cancelAnimationFrame(animationFrameId);
  }, [active, elapsedSeconds, stageRef]);
}

function ChannelTransformer({
  active,
  avatar,
  color,
  delay,
  elapsedSeconds,
  label,
  logo,
  message,
  y,
}: (typeof channels)[number] & { active: boolean; elapsedSeconds: number }) {
  const rowCenter = y + 30;
  const waveFrames = channelWaveFrames.get(label)!;
  const waveWindow = MESSAGE_CONSUMED_AT - MESSAGE_REACHES_TRANSFORMER;
  const waveTimes = [
    0,
    MESSAGE_REACHES_TRANSFORMER,
    ...Array.from(
      { length: WAVE_RANDOM_STEPS },
      (_, step) => MESSAGE_REACHES_TRANSFORMER + ((step + 1) / (WAVE_RANDOM_STEPS + 1)) * waveWindow,
    ),
    MESSAGE_CONSUMED_AT,
    1,
  ];
  const reversedWords = message.split(/\s+/).reverse();
  const channelLogoSize = label === "WhatsApp"
    ? "78%"
    : label === "Email" || label === "Web Chat"
      ? "82%"
      : "76%";
  const messageTimes = [
    0,
    MESSAGE_REACHES_TRANSFORMER,
    MESSAGE_CONSUMED_AT,
    1,
  ];
  const initialMessageProgress = elapsedSeconds < delay
    ? 0
    : positiveModulo(elapsedSeconds - delay, CHANNEL_CYCLE_DURATION) /
      CHANNEL_CYCLE_DURATION;
  const initialMessageX = `${interpolateNumberKeyframe(
    [-125, 0, 100, 100],
    messageTimes,
    initialMessageProgress,
  )}%`;
  const animationDelay = resumedAnimationDelay(
    delay,
    CHANNEL_CYCLE_DURATION,
    elapsedSeconds,
  );

  return (
    <div aria-hidden="true" className="absolute inset-0">
      <div
        className="absolute z-10 overflow-hidden"
        style={{
          ...rectStyle(61, rowCenter - 17, 123, 34),
          maskImage: "linear-gradient(90deg,transparent 0%,#000 22%,#000 100%)",
          WebkitMaskImage: "linear-gradient(90deg,transparent 0%,#000 22%,#000 100%)",
        }}
      >
        <motion.div
          data-channel-message={label}
          className="absolute inset-y-0 right-0 flex items-center gap-2 whitespace-nowrap"
          initial={{ opacity: 1, x: initialMessageX }}
          animate={
            active
              ? {
                  opacity: 1,
                  x: ["-125%", "0%", "100%", "100%"],
                }
              : { opacity: 1, x: initialMessageX }
          }
          transition={
            active
              ? {
                  delay: animationDelay,
                  duration: CHANNEL_CYCLE_DURATION,
                  ease: "linear",
                  repeat: Infinity,
                  times: messageTimes,
                }
              : { duration: 0 }
          }
        >
          <span
            className="inline-flex items-center gap-1 text-[11px] font-extrabold leading-none text-[#3b394a] sm:text-[12px]"
            style={{
              paintOrder: "stroke fill",
              textShadow: "0 1px 2px rgba(49,43,70,.16)",
              WebkitTextStroke: "1.25px rgba(255,255,255,.98)",
            }}
          >
            {reversedWords.map((word, index) => (
              <span key={`${word}-${index}`} className="inline-block">
                {word}
              </span>
            ))}
          </span>
          <PatientAvatar name={avatar} size={20} />
        </motion.div>
      </div>

      <svg
        aria-hidden="true"
        className="pointer-events-none absolute z-20 overflow-visible"
        preserveAspectRatio="none"
        style={{
          ...rectStyle(182, rowCenter - 44, 66, 61),
          filter: "drop-shadow(0 10px 18px rgba(50,43,78,.10))",
        }}
        viewBox="0 0 66 61"
      >
        <defs>
          <linearGradient
            id={`channel-surface-${label.replace(/\s+/g, "-")}`}
            x1="10"
            x2="56"
            y1="3"
            y2="59"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.97" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0.82" />
          </linearGradient>
        </defs>
        <path
          d="M17 27V12C17 5.4 22.4 0 29 0H37C43.6 0 49 5.4 49 12V27H52C59.7 27 66 33.3 66 41V47C66 54.7 59.7 61 52 61H14C6.3 61 0 54.7 0 47V41C0 33.3 6.3 27 14 27H17Z"
          fill={`url(#channel-surface-${label.replace(/\s+/g, "-")})`}
          stroke="rgba(255,255,255,.96)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div
        data-channel-transformer={label}
        title={`${label} channel`}
        className="absolute z-20 overflow-hidden rounded-[14px]"
        style={rectStyle(182, rowCenter - 17, 66, 34)}
      >
        <motion.span
          className="absolute inset-y-0 -left-6 w-5 skew-x-[-10deg] bg-gradient-to-r from-transparent via-white/90 to-transparent"
          initial={{ opacity: 0, x: 0 }}
          animate={active ? { opacity: [0, 0, 0.92, 0, 0], x: [0, 0, 76, 76, 76] } : { opacity: 0, x: 0 }}
          transition={
            active
              ? {
                  delay: animationDelay,
                  duration: CHANNEL_CYCLE_DURATION,
                  ease: "linear",
                  repeat: Infinity,
                  times: [0, TRANSFORMER_GLOW_START, TRANSFORMER_GLOW_PEAK, TRANSFORMER_GLOW_END, 1],
                }
              : { duration: 0 }
          }
        />

        <div className="relative flex h-full items-center justify-center px-1.5">
          <span
            className="flex h-6 w-full items-center justify-center overflow-hidden rounded-full px-1"
            style={{ backgroundColor: `${color}0d` }}
          >
            <svg
              aria-hidden="true"
              className="h-full w-full overflow-visible"
              preserveAspectRatio="xMidYMid meet"
              viewBox="0 0 42 24"
            >
              <motion.path
                data-channel-wave={label}
                d={waveFrames.paths[0]}
                fill="none"
                stroke={color}
                strokeLinecap="round"
                strokeWidth="2"
                initial={{ d: waveFrames.paths[0], opacity: 0.28 }}
                animate={
                  active
                    ? {
                        d: [...waveFrames.paths],
                        opacity: [...waveFrames.opacities],
                      }
                    : { d: waveFrames.paths[0], opacity: 0.28 }
                }
                transition={
                  active
                    ? {
                        delay: animationDelay,
                        duration: CHANNEL_CYCLE_DURATION,
                        ease: "linear",
                        repeat: Infinity,
                        times: waveTimes,
                      }
                    : { duration: 0 }
                }
              />
            </svg>
          </span>
        </div>
      </div>

      <span
        data-channel-logo={label}
        className="absolute z-30 flex items-center justify-center"
        style={rectStyle(199, rowCenter - 44, 32, 30)}
      >
        <motion.img
          src={logo}
          alt=""
          draggable={false}
          className="relative z-10 object-contain"
          style={{
            height: channelLogoSize,
            width: channelLogoSize,
          }}
          initial={{ scale: 1 }}
          animate={active ? { scale: [1, 1, 0.9, 1, 1] } : { scale: 1 }}
          transition={
            active
              ? {
                  delay: animationDelay,
                  duration: CHANNEL_CYCLE_DURATION,
                  ease: "linear",
                  repeat: Infinity,
                  times: [0, LOGO_PULSE_START, LOGO_PULSE_PEAK, LOGO_PULSE_END, 1],
                }
              : { duration: 0 }
          }
        />
      </span>
    </div>
  );
}

function CircularAiCore({
  active,
  elapsedSeconds,
  reducedMotion,
}: {
  active: boolean;
  elapsedSeconds: number;
  reducedMotion: boolean;
}) {
  return (
    <div
      className="pointer-events-none absolute z-20 isolate flex items-center justify-center overflow-visible rounded-full"
      style={rectStyle(313, 150, 170, 170)}
    >
      <motion.span
        data-ai-logo-glow="ambient"
        aria-hidden="true"
        className="absolute inset-0 z-0 flex items-center justify-center"
        initial={{ opacity: 0.58, scale: 0.98 }}
        animate={
          !reducedMotion && active
            ? {
                opacity: [0.52, 0.76, 0.52],
                scale: [0.98, 1.035, 0.98],
              }
            : { opacity: 0.58, scale: 1 }
        }
        transition={
          !reducedMotion && active
            ? {
                delay: resumedAnimationDelay(0, 3.2, elapsedSeconds),
                duration: 3.2,
                ease: "easeInOut",
                repeat: Infinity,
              }
            : { duration: 0 }
        }
      >
        <span
          aria-hidden="true"
          className="absolute inset-[9%] rounded-full"
          style={{
            background:
              "radial-gradient(circle,rgba(180,116,255,.32) 0%,rgba(137,74,225,.18) 38%,rgba(74,190,245,.08) 57%,transparent 74%)",
          }}
        />
        <img
          src={clawEaseMark}
          alt=""
          draggable={false}
          className="absolute h-[58%] w-[58%] object-contain opacity-20 blur-[14px]"
        />
        <img
          src={clawEaseMark}
          alt=""
          draggable={false}
          className="absolute h-[52%] w-[52%] object-contain opacity-30 blur-[6px]"
        />
      </motion.span>

      <span
        data-ai-core-orbit-guide
        aria-hidden="true"
        className="absolute inset-[4%] z-[2] rounded-full opacity-0"
      />

      <motion.img
        data-ai-core-logo
        src={clawEaseMark}
        alt=""
        className="relative z-10 h-[40%] w-[40%] -translate-y-[11px] object-contain"
        style={{
          filter:
            "drop-shadow(0 0 6px rgba(128,64,220,.28)) drop-shadow(0 0 14px rgba(72,190,255,.16))",
        }}
        draggable={false}
        initial={{ scale: 1 }}
        animate={!reducedMotion && active ? { scale: [1, 1.025, 1] } : { scale: 1 }}
        transition={!reducedMotion && active ? { delay: resumedAnimationDelay(0, 2.8, elapsedSeconds), duration: 2.8, ease: "easeInOut", repeat: Infinity } : { duration: 0 }}
      />

    </div>
  );
}

export default function DentalHeroFlowDom() {
  const stageRef = useRef<HTMLDivElement>(null);
  const reducedMotion = Boolean(useReducedMotion());
  const pageActive = usePageActivity();
  const isInView = useInView(stageRef, { amount: 0.05, initial: true });
  const timeline = usePausableTimeline(
    !reducedMotion && isInView && pageActive,
    INITIAL_TIMELINE_OFFSET_SECONDS,
  );
  const active = timeline.running;
  const mountedAvatarEntries = useMountedAvatarDescriptors(
    active,
    timeline.elapsedSeconds,
  );
  const activePmsDestination = usePmsDestination(
    active,
    timeline.elapsedSeconds,
    reducedMotion,
    "arrival",
  );
  const prelitPmsDestination = usePmsDestination(
    active,
    timeline.elapsedSeconds,
    reducedMotion,
    "activation",
  );
  const pmsLogoPulse = usePmsLogoPulse(
    active,
    timeline.elapsedSeconds,
    reducedMotion,
  );
  useRouteLightField(stageRef, active, timeline.elapsedSeconds);

  return (
    <div
      ref={stageRef}
      data-flow-active={active ? "true" : "false"}
      data-flow-elapsed={timeline.elapsedSeconds.toFixed(3)}
      data-flow-mounted-avatars={mountedAvatarEntries.length}
      data-active-pms={activePmsDestination}
      data-prelit-pms={prelitPmsDestination}
      role="img"
      aria-label="Patient messages arrive in groups of four. Each group becomes four patient avatars, circles the ClawEase AI processing core, and continues into Dentaltap, DentOS, then CareStack. The next destination lights before the avatars take its route, then becomes active when the first avatar arrives."
      className="relative isolate mx-auto mb-8 aspect-[720/430] w-full max-w-[700px] overflow-visible font-sans lg:mb-6 xl:mb-0 xl:-translate-x-14"
      style={{ containerType: "size" }}
    >
      <div
        data-flow-ambient
        aria-hidden="true"
        className="pointer-events-none absolute z-0 blur-[24px]"
        style={{
          inset: "-12% -14%",
          background:
            "radial-gradient(ellipse at 55% 50%,rgba(109,53,207,.10) 0%,rgba(109,53,207,.045) 42%,rgba(109,53,207,0) 74%)",
        }}
      />

      {channels.map((channel) => (
        <ChannelTransformer
          key={channel.label}
          {...channel}
          active={active}
          elapsedSeconds={timeline.elapsedSeconds}
        />
      ))}

      <CircularAiCore
        active={active}
        elapsedSeconds={timeline.elapsedSeconds}
        reducedMotion={reducedMotion}
      />

      {pmsLogos.map((brand) => (
        <PmsConstellationLogo
          key={brand.label}
          brand={brand}
          flowActive={active}
          isActive={brand.label === activePmsDestination}
          isPrelit={brand.label === prelitPmsDestination}
          pulseSerial={pmsLogoPulse.label === brand.label ? pmsLogoPulse.serial : 0}
          reducedMotion={reducedMotion}
        />
      ))}

      <RouteLightFieldDots />

      {mountedAvatarEntries.map(({ animationElapsedSeconds, descriptor }) => (
        <FlowingAvatar
          key={descriptor.id}
          active={active}
          currentElapsedSeconds={timeline.elapsedSeconds}
          descriptor={descriptor}
          elapsedSeconds={animationElapsedSeconds}
        />
      ))}
    </div>
  );
}
